/**
 * クラウドデータ管理サービス
 * Cloudflare KV との API 通信を担う
 */

const WORKER_BASE_URL = 'https://bkms-worker.jajajunk.workers.dev';

/**
 * API エラーレスポンスの詳細を取得する
 * @param {Response} response fetch レスポンス
 * @returns {Promise<string>} エラーメッセージ
 */
async function extractApiError(response) {
  const text = await response.text();
  try {
    const json = JSON.parse(text);
    return json.error || text;
  } catch {
    return text || response.statusText;
  }
}

/**
 * ブックマークデータを Cloudflare KV に保存する
 * @param {string} id クラウド識別 ID
 * @param {Array} bookmarks ブックマーク配列
 * @param {string} pinCode PINコード
 * @returns {Promise<object>} API 応答データ
 */
export async function saveToCloud(id, bookmarks, pinCode) {
  const response = await fetch(`${WORKER_BASE_URL}/api/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ id, data: bookmarks, pinCode }),
    mode: 'cors'
  });

  if (!response.ok) {
    const detail = await extractApiError(response);
    throw new Error(`API エラー: ${response.status} - ${detail}`);
  }

  return response.json();
}

/**
 * クラウドAPIエラーを生成する（エラー種別情報を付与）
 * @param {string} message エラーメッセージ
 * @param {object} options エラー付加情報
 * @returns {Error} 種別情報付きエラー
 */
function createCloudError(message, options = {}) {
  const error = new Error(message);
  error.status = options.status || 0;
  error.isNetworkError = options.isNetworkError || false;
  error.isRetryable = options.isRetryable || false;
  error.isAuthError = options.isAuthError || false;
  error.isLocked = options.isLocked || false;
  error.attemptCount = options.attemptCount || undefined;
  error.maxAttempts = options.maxAttempts || undefined;
  error.retryAfterSeconds = options.retryAfterSeconds || undefined;
  return error;
}

/**
 * Cloudflare KV からブックマークデータを取得する
 * @param {string} id クラウド識別 ID
 * @param {string} pinCode PINコード
 * @returns {Promise<Array>} ブックマーク配列
 */
export async function fetchFromCloud(id, pinCode) {
  const formattedPin = String(pinCode).padStart(4, '0');
  const url = `${WORKER_BASE_URL}/api/data/${id}?pin=${encodeURIComponent(formattedPin)}`;

  let response;
  try {
    response = await fetch(url);
  } catch (fetchError) {
    // ネットワークエラー（DNS解決失敗、タイムアウト、オフラインなど）
    throw createCloudError(`ネットワークエラー: ${fetchError.message}`, {
      isNetworkError: true,
      isRetryable: true
    });
  }

  if (!response.ok) {
    const status = response.status;
    let detail = '';
    let attemptCount, maxAttempts, retryAfterSeconds;
    try {
      const text = await response.text();
      const json = JSON.parse(text);
      detail = json.error || text;
      attemptCount = json.attemptCount;
      maxAttempts = json.maxAttempts;
      retryAfterSeconds = json.retryAfterSeconds;
    } catch {
      detail = detail || response.statusText;
    }
    const isPinLockout = status === 429 && attemptCount !== undefined;
    throw createCloudError(`API呼び出しエラー: ${status} - ${detail}`, {
      status,
      isAuthError: status === 401 || status === 403 || isPinLockout,
      isRetryable: (status >= 500 || status === 429) && !isPinLockout,
      isLocked: isPinLockout,
      attemptCount,
      maxAttempts,
      retryAfterSeconds
    });
  }

  const responseText = await response.text();

  // HTML エラーページの検出
  const trimmed = responseText.trim().toLowerCase();
  if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
    throw createCloudError('APIがHTMLを返しました。データ形式が不正です。', {
      isRetryable: true
    });
  }

  try {
    const data = JSON.parse(responseText);
    // 新形式（オブジェクト: { bookmarks, customColors }）と旧形式（配列）の両方を処理
    if (Array.isArray(data)) {
      return { bookmarks: data, customColors: [] };
    }
    if (data && Array.isArray(data.bookmarks)) {
      return { bookmarks: data.bookmarks, customColors: data.customColors || [] };
    }
    throw createCloudError('取得したデータの形式が不正です');
  } catch (e) {
    if (e.isRetryable !== undefined) throw e; // 既にcreateCloudErrorで作成済み
    throw createCloudError('JSONデータのパースに失敗しました');
  }
}

/**
 * Cloudflare KV からブックマークデータを削除する
 * @param {string} id クラウド識別 ID
 * @param {string} pinCode PINコード
 * @returns {Promise<object>} API 応答データ
 */
export async function deleteFromCloud(id, pinCode) {
  const response = await fetch(`${WORKER_BASE_URL}/api/delete/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ pin: pinCode }),
    mode: 'cors'
  });

  if (!response.ok) {
    const detail = await extractApiError(response);
    throw new Error(`API エラー: ${response.status} - ${detail}`);
  }

  return response.json();
}

/**
 * ブックマークを公開する
 * @param {string} sourceId クラウド識別 ID
 * @param {string} pinCode PINコード（オーナー認証用）
 * @param {string} snsUrl SNSアカウントURL（任意）
 * @param {string} comment コメント（任意）
 * @param {string} ogpImage OGP画像のbase64データ（任意）
 * @returns {Promise<object>} { success, publishId, publishUrl }
 */
export async function publishBookmarks(sourceId, pinCode, snsUrl, comment, ogpImage) {
  const formattedPin = String(pinCode).padStart(4, '0');
  const response = await fetch(`${WORKER_BASE_URL}/api/publish/${sourceId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      pin: formattedPin,
      snsUrl: snsUrl || '',
      comment: comment || '',
      ogpImage: ogpImage || ''
    }),
    mode: 'cors'
  });

  if (!response.ok) {
    const detail = await extractApiError(response);
    throw new Error(`公開エラー: ${response.status} - ${detail}`);
  }

  return response.json();
}

/**
 * 公開データを取得する（PINコード不要）
 * @param {string} publishId 公開用ID
 * @returns {Promise<object>} { bookmarks, snsUrl, snsPlatform, snsUsername, publishedAt }
 */
export async function fetchPublishedData(publishId) {
  const response = await fetch(`${WORKER_BASE_URL}/api/publish/${publishId}`);

  if (!response.ok) {
    const detail = await extractApiError(response);
    throw new Error(`公開データ取得エラー: ${response.status} - ${detail}`);
  }

  return response.json();
}

/**
 * ブックマークの公開を停止する
 * @param {string} publishId 公開用ID
 * @param {string} sourceId クラウド識別 ID
 * @param {string} pinCode PINコード（オーナー認証用）
 * @returns {Promise<object>} API 応答データ
 */
export async function unpublishBookmarks(publishId, sourceId, pinCode) {
  const formattedPin = String(pinCode).padStart(4, '0');
  const response = await fetch(`${WORKER_BASE_URL}/api/publish/${publishId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ sourceId, pin: formattedPin }),
    mode: 'cors'
  });

  if (!response.ok) {
    const detail = await extractApiError(response);
    throw new Error(`非公開エラー: ${response.status} - ${detail}`);
  }

  return response.json();
}

export default {
  saveToCloud,
  fetchFromCloud,
  deleteFromCloud,
  publishBookmarks,
  fetchPublishedData,
  unpublishBookmarks
};
